/* eslint-disable @typescript-eslint/no-floating-promises */
import {
    IDrivingVehicle,
    IDrivingModel
} from 'components/driving/driving-interface'
import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { AriaAttr, EventType, HtmlAttr, KeyType } from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { CompareEventType, MAX_VEHICLES } from './constants'
import { CarComparisonStore } from '../store'
import { isComparisonResults } from 'utils/page'
import { getResultsUrlPath } from '../util'
import { ModelCard } from '../model-card/model-card'

const { myPicks: className, common: commonClass } = CLASS_NAME

/**
 * Driving My Pick, button and widget, component
 */
export class MyPicks extends ComponentBase {
    private sectionShrank: HTMLDivElement
    private sectionExpanded: HTMLDivElement
    private buttonEdit: HTMLButtonElement
    private buttonContinue: HTMLButtonElement
    private buttonShrink: HTMLButtonElement
    private buttonExpand: HTMLButtonElement
    private buttonLimitExceededModal: HTMLButtonElement
    private buttonCompareNow: HTMLButtonElement
    private selections: HTMLDivElement
    private numVehicles: HTMLElement[]
    private title: HTMLHeadingElement
    private sponsored: HTMLDivElement
    private sponsoredCard: HTMLDivElement

    /**
     * Get the information (You can add up to n more) HTML
     *
     * @returns {Nullable<HTMLDivElement>}
     */
    private get information(): Nullable<HTMLDivElement> {
        return this.getElement<HTMLDivElement>(
            `.${className.information}`,
            this.sectionExpanded
        )
    }

    /**
     * Get the information (You don’t have any cars to compare) HTML
     *
     * @returns {Nullable<HTMLDivElement>}
     */
    private get emptyInformation(): Nullable<HTMLDivElement> {
        return this.getElement<HTMLDivElement>(
            `.${className.informationEmpty}`,
            this.sectionExpanded
        )
    }

    /**
     * Get the information (You can add up to n more) HTML
     *
     * @returns {HTMLDivElement[]}
     */
    private get loading(): HTMLDivElement[] {
        return this.getElements<HTMLDivElement>(
            `.${className.loading}`,
            this.sectionExpanded
        )
    }

    constructor(private container: HTMLElement) {
        super()

        if (!this.initHTMLElements()) {
            return
        }
        this.container.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => {
                if (e.key === KeyType.ESC) {
                    e.preventDefault()
                    this.hideExpanded()
                    this.restoreFocus()
                }
            }
        )

        // Toggle button event listeners
        this.buttonExpand.addEventListener(EventType.CLICK, () =>
            this.showExpanded()
        )
        this.buttonShrink.addEventListener(EventType.CLICK, () =>
            this.hideExpanded()
        )
        this.buttonEdit.addEventListener(EventType.CLICK, () =>
            this.showExpanded()
        )
        this.buttonContinue.addEventListener(EventType.CLICK, () => {
            this.hideExpanded()
            this.restoreFocus()
        })
        this.buttonLimitExceededModal.addEventListener(EventType.CLICK, () => {
            CarComparisonStore.getInstance()
                .getLimitExceededModal()
                .closeModal()
            this.showExpanded()
        })

        this.initStore()

        CarComparisonStore.getInstance().myPicks = this
        // Init sponsored vehicle
        CarComparisonStore.getInstance().initSponsoredVehicle()

        // Hide on comparison result page
        if (isComparisonResults()) {
            this.container.setAttribute(HtmlAttr.HIDDEN, 'hidden')
        }
    }

    /**
     * Show expanded component
     */
    private showExpanded(): void {
        this.sectionExpanded.classList.remove(className.expandedHide)
        this.sectionExpanded.classList.add(className.expandedShow)
        this.sectionShrank.setAttribute(AriaAttr.HIDDEN, 'true')
        this.sectionExpanded.setAttribute(AriaAttr.HIDDEN, 'false')
        this.buttonShrink.setAttribute(AriaAttr.EXPANDED, 'true')
        this.buttonExpand.setAttribute(AriaAttr.EXPANDED, 'true')
        this.title.tabIndex = 0
        this.title.focus()
        this.getElements('button, a', this.sectionShrank).forEach(
            (element) => (element.tabIndex = -1)
        )
        this.getElements('button, a', this.sectionExpanded).forEach((element) =>
            element.removeAttribute(HtmlAttr.TAB_INDEX)
        )
    }

    /**
     * Hide expanded component and back to the button
     */
    private hideExpanded(): void {
        this.sectionExpanded.classList.add(className.expandedHide)
        this.sectionExpanded.classList.remove(className.expandedShow)
        this.sectionShrank.setAttribute(AriaAttr.HIDDEN, 'false')
        this.sectionExpanded.setAttribute(AriaAttr.HIDDEN, 'true')
        this.buttonShrink.setAttribute(AriaAttr.EXPANDED, 'false')
        this.buttonExpand.setAttribute(AriaAttr.EXPANDED, 'false')
        this.buttonExpand.focus()
        this.title.tabIndex = -1
        this.getElements('button, a', this.sectionExpanded).forEach(
            (element) => (element.tabIndex = -1)
        )
        this.getElements('button, a', this.sectionShrank).forEach((element) =>
            element.removeAttribute(HtmlAttr.TAB_INDEX)
        )
    }

    /**
     * Hide entire region in DOM
     */
    private hideMenu(): void {
        this.container.classList.add(CLASS_NAME.common.hidden)
    }

    /**
     * Show entire region in DOM
     */
    private showMenu(): void {
        this.container.classList.remove(CLASS_NAME.common.hidden)
    }

    /**
     * when closing return to last focused element or main container
     */
    private restoreFocus(): void {
        const focusCard = CarComparisonStore.getInstance()
            .lastActiveCard as ModelCard
        if (focusCard) {
            focusCard.focusCard()
        } else if (this.main) {
            this.main.focus()
        }
    }

    /**
     * Initialize HTML DOMs
     *
     * @returns {boolean} indicates mandatory DOMs exist
     */
    private initHTMLElements(): boolean {
        const sectionShrank = this.getElement<HTMLDivElement>(
            `.${className.shrink}`,
            this.container
        )
        const sectionExpanded = this.getElement<HTMLDivElement>(
            `.${className.expanded}`,
            this.container
        )
        const buttonExpand = this.getElement<HTMLButtonElement>(
            `.${className.expandButton}`,
            sectionShrank
        )
        const buttonShrink = this.getElement<HTMLButtonElement>(
            `.${className.shrinkButton}`,
            sectionExpanded
        )
        const selections = this.getElement<HTMLDivElement>(
            `.${className.selections}`,
            sectionExpanded
        )
        this.numVehicles = this.getElements<HTMLSpanElement>(
            `.${className.numVehicles}`,
            this.container
        )
        const title = this.getElement<HTMLHeadingElement>(
            `.${className.expandedTitle}`,
            sectionExpanded
        )
        // TODO
        const buttonEdit = this.getElement<HTMLButtonElement>(
            `.${className.buttonEdit}`,
            sectionShrank
        )
        const buttonContinue = this.getElement<HTMLButtonElement>(
            `.${className.buttonContinue}`,
            sectionExpanded
        )
        const buttonLimitExceededModal = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.modal._} .${className.buttonLimitExceededModal}`,
            document.body
        )
        const buttonCompareNow = this.getElement<HTMLButtonElement>(
            `.${className.buttonCompareNow}`,
            sectionExpanded
        )
        const sponsored = this.getElement<HTMLDivElement>(
            `.${className.sponsored}`,
            sectionExpanded
        )
        const sponsoredCard = this.getElement<HTMLDivElement>(
            `.${className.sponsoredCard}`,
            sectionExpanded
        )

        if (
            !sectionShrank ||
            !sectionExpanded ||
            !buttonShrink ||
            !buttonExpand ||
            !selections ||
            !title ||
            !buttonEdit ||
            !buttonContinue ||
            !buttonLimitExceededModal ||
            !buttonCompareNow ||
            !sponsoredCard ||
            !sponsored
        ) {
            return false
        }

        this.sectionShrank = sectionShrank
        this.sectionExpanded = sectionExpanded
        this.buttonShrink = buttonShrink
        this.buttonExpand = buttonExpand
        this.selections = selections
        this.title = title
        this.buttonEdit = buttonEdit
        this.buttonContinue = buttonContinue
        this.buttonLimitExceededModal = buttonLimitExceededModal
        this.buttonCompareNow = buttonCompareNow
        this.sponsoredCard = sponsoredCard
        this.sponsored = sponsored

        return true
    }

    /**
     * Reconstruct this component from store
     */
    private initStore(): void {
        // Add cards from store
        const vehicles = CarComparisonStore.getInstance().get()
        if (vehicles.length) {
            vehicles.forEach((vehicle) => {
                if (vehicle.item) {
                    this.addCard(vehicle.item)
                } else {
                    CarComparisonStore.getInstance().remove(vehicle.id)
                }
            })
        } else {
            this.handleInformation()
        }

        // Event listeners to render the container
        // Loading...
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_ADD_INIT,
            (e: CustomEvent<number>) => {
                this.showMenu()
                this.showExpanded()
                this.addLoading(e.detail)
            }
        )
        // Loaded! :D
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_ADD_SUCCESS,
            (e: CustomEvent<IDrivingVehicle>) => {
                this.removeCard(e.detail.id)
                this.addCard(e.detail)
            }
        )
        // Failed :(
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_ADD_FAILED,
            (e: CustomEvent<number>) => {
                this.removeCard(e.detail)
            }
        )
        // Removed
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_REMOVE,
            (e: CustomEvent<number>) => {
                this.removeCard(e.detail)
            }
        )
        // Out of range
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_ADD_OUT_OF_RANGE,
            () => {
                CarComparisonStore.getInstance()
                    .getLimitExceededModal()
                    .openModal()
            }
        )
    }

    /**
     * Add loading indicator to the container
     *
     * @param {number} id
     */
    private addLoading(id: number): void {
        const loading = document.createElement('div')
        loading.classList.add(className.loading)
        loading.id = `my-picks-vehicle-${id}`
        loading.innerText = 'Loading...'
        this.selections.append(loading)
        this.handleInformation()
    }

    /**
     * Get the card image, name, and trim
     *
     * @param {IDrivingVehicle} vehicle
     * @return {Object}
     */
    private getCardBaseInfo(
        vehicle: IDrivingVehicle
    ): {
        img: HTMLImageElement | HTMLSpanElement
        name: HTMLHeadingElement
        trim: HTMLParagraphElement
    } {
        let img
        const model = vehicle.model as IDrivingModel
        if (model.images && model.images[0]) {
            img = document.createElement('img')
            img.src = model.images[0].url
            img.alt = ''
            img.height = 54
            img.width = 72
            img.classList.add(className.cardImage)
            img.loading = 'lazy'
        } else {
            img = document.createElement('span')
            img.classList.add(className.cardImage)
        }

        const vehicleName = this.getVehicleName(vehicle)
        const vehicleTrim = this.getVehicleTrim(vehicle)

        // Card.Name Element
        const name = document.createElement('h3')
        name.classList.add(className.cardName)
        name.innerText = vehicleName

        // Card.Trim Element
        const trim = document.createElement('p')
        trim.classList.add(className.cardTrim)
        trim.innerText = vehicleTrim

        return {
            img,
            name,
            trim
        }
    }

    /**
     * Get the vehicle name with year and make
     *
     * @param {IDrivingVehicle} vehicle
     * @return {string}
     */
    private getVehicleName(vehicle: IDrivingVehicle): string {
        return `${vehicle.year} ${vehicle.model.make.name} ${vehicle.model.name}`
    }

    /**
     * Get the trim name includes the Base Trim
     *
     * @param {IDrivingVehicle} vehicle
     * @return {string}
     */
    private getVehicleTrim(vehicle: IDrivingVehicle): string {
        return vehicle.name == '-' ? 'Base Trim' : vehicle.name
    }

    /**
     * Add card to the container
     *
     * @param {IDrivingVehicle} vehicle Vehicle to add
     */
    private addCard(vehicle: IDrivingVehicle): void {
        const isMenuOpen = this.sectionExpanded.classList.contains(
            className.expandedShow
        )

        // Card Element
        const card = document.createElement('div')
        card.classList.add(className.card, className.cardAdded)
        card.id = `my-picks-vehicle-${vehicle.id}`

        // Card.Image Element
        const baseInfo = this.getCardBaseInfo(vehicle)
        card.append(baseInfo.img, baseInfo.name, baseInfo.trim)

        // Card.Remove button Element
        const remove = document.createElement('button')
        remove.classList.add(
            CLASS_NAME.common.button,
            CLASS_NAME.common.buttonTransparent
        )
        remove.setAttribute(AriaAttr.CONTROLS, `my-picks-vehicle-${vehicle.id}`)
        remove.setAttribute(
            AriaAttr.LABEL,
            `Remove ${this.getVehicleName(vehicle)} ${this.getVehicleTrim(
                vehicle
            )}`
        )
        if (!isMenuOpen) {
            remove.tabIndex = -1
        }
        remove.type = 'button'

        const removeIcon = document.createElement('div')
        removeIcon.classList.add(className.buttonRemoveIcon)
        remove.append(removeIcon)

        const removeText = document.createElement('span')
        removeText.classList.add(className.buttonRemoveText)
        removeText.innerText = 'Remove'
        removeText.id = `my-picks-vehicle-remove-${vehicle.id}`
        remove.append(removeText)

        card.append(remove)

        remove.addEventListener(EventType.CLICK, () => {
            CarComparisonStore.getInstance().remove(vehicle.id)
        })

        // Append and handle information and number of vehicles
        this.selections.append(card)
        setTimeout(() => {
            card.classList.remove(className.cardAdded)
        }, 1)
        this.handleInformation()
    }

    /**
     * Remove card from the component
     *
     * @param {number} id Vehicle to remove
     */
    private removeCard(id: number): void {
        const card = this.getElement(`#my-picks-vehicle-${id}`, this.selections)
        if (card) {
            card.remove()
        }
        this.handleInformation()
    }

    /**
     * Change the information element's text and change the number of vehicles indicator
     */
    private handleInformation(): void {
        const vehicles = CarComparisonStore.getInstance().get()
        const resultUrl = getResultsUrlPath(
            vehicles.map((vehicle) => vehicle.item)
        )
        const numVehicles = vehicles
            .map((vehicle) => vehicle.item)
            .filter((item) => item).length

        if (resultUrl) {
            this.buttonCompareNow.setAttribute(HtmlAttr.HREF, resultUrl)
        }

        if (numVehicles === 0) {
            this.hideMenu()
        } else {
            this.showMenu()
        }

        // Change the number of vehicles indicator
        this.numVehicles.forEach(
            (element: HTMLElement) =>
                (element.innerText = numVehicles.toString())
        )
        this.sectionExpanded.setAttribute(
            ATTR_NAME.driving.myPicks.numVehicles,
            numVehicles.toString()
        )

        // Sort loading to the end
        this.loading.forEach((element: HTMLElement) => {
            this.selections.appendChild(element)
        })

        // Change the information element's text
        if (this.information) {
            this.information.remove()
        }

        if (this.emptyInformation) {
            this.emptyInformation.remove()
        }

        const remains = MAX_VEHICLES - numVehicles

        if (remains <= 0) {
            return
        }

        if (numVehicles === 0) {
            this.renderEmptyInformation()
        } else {
            const html = document.createElement('div')
            html.classList.add(className.information)
            html.innerText = `You can add up to ${remains} more`
            html.setAttribute(AriaAttr.LIVE, 'polite')
            this.selections.append(html)
            this.buttonCompareNow.setAttribute(AriaAttr.LIVE, 'polite')
        }

        if (numVehicles === 0) {
            this.buttonCompareNow.innerText = 'Pick more cars'
            this.buttonCompareNow.classList.add(commonClass.buttonDisabled)
        } else if (numVehicles === 1) {
            this.buttonCompareNow.innerText = 'Pick 1 more to compare'
            this.buttonCompareNow.classList.add(commonClass.buttonDisabled)
        } else {
            this.buttonCompareNow.innerText = 'Compare Now'
            this.buttonCompareNow.classList.remove(commonClass.buttonDisabled)
        }

        if (this.sectionExpanded.classList.contains(className.expandedShow)) {
            if (numVehicles == 1) {
                this.buttonContinue.focus()
            } else {
                this.buttonCompareNow.focus()
            }
        }
    }

    /**
     * Render the empty vehicle information
     */
    private renderEmptyInformation(): void {
        const card = document.createElement('div')
        card.classList.add(className.informationEmpty)

        const paragraph = document.createElement('p')
        // eslint-disable-next-line quotes
        paragraph.innerText = `You don't have any cars to compare`
        card.append(paragraph)

        const button = document.createElement('button')
        button.type = 'button'
        button.classList.add(
            CLASS_NAME.common.button,
            CLASS_NAME.common.buttonTransparent,
            className.buttonFindMore
        )
        button.innerText = 'Find more cars to add'
        card.append(button)

        this.selections.append(card)
    }

    /**
     * Replace the sponsored vehicle section
     *
     * @param {IDrivingVehicle} vehicle
     */
    public renderSponsoredVehicle(vehicle?: IDrivingVehicle): void {
        this.sponsoredCard.innerHTML = ''

        if (!vehicle || !Object.keys(vehicle).length) {
            this.sponsored.setAttribute(HtmlAttr.HIDDEN, 'hidden')
            return
        }

        this.sponsored.removeAttribute(HtmlAttr.HIDDEN)
        const baseInfo = this.getCardBaseInfo(vehicle)
        this.sponsoredCard.append(baseInfo.img, baseInfo.name, baseInfo.trim)
    }
}
